import { useCallback, useSyncExternalStore } from 'react';
import { fetchFullListingDetails, getListingHomeId, getListingKey } from './listingDetailsApi.js';

// Favourite listings, kept in this browser (localStorage) so they survive reloads and show without
// a search. Each favourite is a trimmed copy of the listing (one photo, no floor plans), so the
// favourites tab and popup work even after the listing is gone from the site. A listing that the
// nightly scrape removed is kept and marked `favoriteRemoved` (see checkFavorites), never dropped
// silently. Shared by kartvy and listvy; every tab of the site stays in sync through the storage event.

const STORAGE_KEY = 'listing_favorites_v1';
const MAX_FAVORITES = 200;
// How often one favourite is checked against the API for being removed.
const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;
const CHECK_CONCURRENCY = 3;

const listeners = new Set();
let favorites = null;

// "bostad/123" / "annons/456" (see getListingKey): the bare number used before could point at two listings.
export const getFavoriteKey = (listing) => {
    const key = getListingKey(listing);
    if (key) return key;
    const id = String(listing?.id || '').trim();
    return id ? `id:${id}` : '';
};

const readStorage = () => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
        if (!Array.isArray(parsed)) return [];
        // Favourites saved with a number key get their path key (from the saved listingUrl); duplicates go.
        const seen = new Set();
        return parsed
            .filter((item) => item && typeof item === 'object' && item.favoriteKey)
            .map((item) => {
                const key = getListingKey(item);
                return key && key !== item.favoriteKey ? { ...item, favoriteKey: key } : item;
            })
            .filter((item) => {
                if (seen.has(item.favoriteKey)) return false;
                seen.add(item.favoriteKey);
                return true;
            });
    } catch (error) {
        return [];
    }
};

const getFavorites = () => {
    if (favorites == null) favorites = readStorage();
    return favorites;
};

const setFavorites = (next) => {
    favorites = next;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
        // Storage full or blocked (private mode): favourites still work until the page is closed.
    }
    listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
    listeners.add(listener);
    const handleStorage = (event) => {
        if (event.key !== STORAGE_KEY) return;
        favorites = readStorage();
        listener();
    };
    window.addEventListener('storage', handleStorage);
    return () => {
        listeners.delete(listener);
        window.removeEventListener('storage', handleStorage);
    };
};

// What is stored: the listing without its photo list and floor plans (one photo for the card).
const toSnapshot = (listing, favoriteKey) => {
    const { images, planritning, ...rest } = listing || {};
    const firstImage = Array.isArray(images) ? images.find(Boolean) : null;
    let snapshot;
    try {
        snapshot = JSON.parse(JSON.stringify({ ...rest, images: firstImage ? [firstImage] : [] }));
    } catch (error) {
        snapshot = { id: rest.id, homeId: rest.homeId, address: rest.address, images: [] };
    }
    return {
        ...snapshot,
        homeId: snapshot.homeId || getListingHomeId(listing),
        favoriteKey,
        favoriteSavedAt: new Date().toISOString(),
        favoriteCheckedAt: Date.now(),
        favoriteRemoved: false,
    };
};

export const isFavoriteListing = (listing) => {
    const key = getFavoriteKey(listing);
    return Boolean(key) && getFavorites().some((item) => item.favoriteKey === key);
};

// Returns true when the listing is a favourite afterwards.
export const toggleFavoriteListing = (listing) => {
    const key = getFavoriteKey(listing);
    if (!key) return false;
    const current = getFavorites();
    if (current.some((item) => item.favoriteKey === key)) {
        setFavorites(current.filter((item) => item.favoriteKey !== key));
        return false;
    }
    setFavorites([toSnapshot(listing, key), ...current].slice(0, MAX_FAVORITES));
    return true;
};

// Marks a favourite as no longer for sale (the API answered 404 for it).
export const markFavoriteRemoved = (listing) => {
    const key = getFavoriteKey(listing);
    const current = getFavorites();
    if (!key || !current.some((item) => item.favoriteKey === key && !item.favoriteRemoved)) return;
    setFavorites(current.map((item) => (
        item.favoriteKey === key ? { ...item, favoriteRemoved: true, favoriteCheckedAt: Date.now() } : item
    )));
};

// Checks favourites not checked in the last CHECK_INTERVAL_MS: a 404 marks one removed, a found one
// gets its price and status tag refreshed. Network errors change nothing, so a bad connection never
// marks a listing as removed.
let checkRunning = false;
export const checkFavorites = async () => {
    if (checkRunning) return;
    const due = getFavorites().filter((item) => (
        getListingKey(item) && Date.now() - Number(item.favoriteCheckedAt || 0) > CHECK_INTERVAL_MS
    ));
    if (!due.length) return;
    checkRunning = true;
    const updates = new Map();
    try {
        const queue = [...due];
        const worker = async () => {
            while (queue.length) {
                const item = queue.shift();
                try {
                    const details = await fetchFullListingDetails(getListingKey(item));
                    updates.set(item.favoriteKey, {
                        favoriteRemoved: false,
                        favoriteCheckedAt: Date.now(),
                        ...(details.price ? { price: details.price } : {}),
                        imgTag: details.imgTag || '',
                    });
                } catch (error) {
                    if (error?.status === 404) {
                        updates.set(item.favoriteKey, { favoriteRemoved: true, favoriteCheckedAt: Date.now() });
                    }
                }
            }
        };
        await Promise.all(Array.from({ length: Math.min(CHECK_CONCURRENCY, due.length) }, worker));
    } finally {
        checkRunning = false;
    }
    if (!updates.size) return;
    // Applied to the current list, so favourites toggled during the check are kept.
    setFavorites(getFavorites().map((item) => (
        updates.has(item.favoriteKey) ? { ...item, ...updates.get(item.favoriteKey) } : item
    )));
};

export const useFavorites = () => {
    const list = useSyncExternalStore(subscribe, getFavorites, () => []);
    const isFavorite = useCallback((listing) => {
        const key = getFavoriteKey(listing);
        return Boolean(key) && list.some((item) => item.favoriteKey === key);
    }, [list]);
    return { favorites: list, isFavorite };
};
